import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { IoArrowBack, IoSaveOutline } from "react-icons/io5";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { toast } from "react-toastify";
import LoadingComponent from "../../Components/LoadingComponent";
import { uploadDocumentToServer } from "../../utils/uploadDocumentToServer";

export default function AddProjectUpdate() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const projectData = location?.state?.projectData || {};
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);

  const handleAddUpdate = async (data) => {
    try {
      setLoading(true);

      let tempDocumentUrl = "";
      if (data?.documentUrl?.[0]) {
        tempDocumentUrl = await uploadDocumentToServer(
          data?.documentUrl?.[0]
        );
      }

      const projectId = Number(projectData?.id || projectData?.Id);
      if (!projectId) {
        toast.error("Project ID is missing. Please go back to project details and try again.");
        setLoading(false);
        return;
      }

      const { data: resData } = await axios.post(
        `${server}/projectUpdate/new`,
        {
          projectId: projectId,
          updateTitle: data?.updateTitle,
          updateDescription: data?.updateDescription,
          documentUrl: tempDocumentUrl || "",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(resData?.message || "Saved successfully");
      setLoading(false);
      reset();
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="p-6 max-w-3xl mx-auto"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            <IoArrowBack className="text-xl" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">
            Add Project Update
          </h1>
        </div>
      </div>

      {/* Project Details */}
      <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">
          Project Details
        </h2>
        <div>
          <p className="text-gray-600 text-sm">
            <strong>Title:</strong> {projectData?.projectName}
          </p>
          <p className="text-gray-600 text-sm mt-1">
            <strong>Description:</strong> {projectData?.description}
          </p>
        </div>
      </div>

      {/* Update Form */}
      <form
        onSubmit={handleSubmit(handleAddUpdate)}
        className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm space-y-5"
      >
        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Update Title
          </label>
          <input
            type="text"
            {...register("updateTitle", {
              required: "Update title is required",
            })}
            placeholder="Enter update title"
            className={`h-11 px-4 w-full border ${
              errors.updateTitle ? "border-red-400" : "border-gray-300"
            } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
          />
          {errors.updateTitle && (
            <p className="text-sm text-red-500 mt-1">
              {errors.updateTitle.message}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Update Description
          </label>
          <textarea
            {...register("updateDescription", {
              required: "Description is required",
            })}
            rows={4}
            placeholder="Describe the update..."
            className={`w-full border ${
              errors.updateDescription ? "border-red-400" : "border-gray-300"
            } rounded-lg p-3 focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
          />
          {errors.updateDescription && (
            <p className="text-sm text-red-500 mt-1">
              {errors.updateDescription.message}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Document
          </label>
          <input
            type="file"
            {...register("documentUrl", {
              required: false,
            })}
            className={`p-4 w-full border ${
              errors.documentUrl ? "border-red-400" : "border-gray-300"
            } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
          />
          {errors.documentUrl && (
            <p className="text-sm text-red-500 mt-1">
              {errors?.documentUrl?.message}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className=" mt-10 flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <IoSaveOutline className="text-lg" />
            Save Update
          </button>
        </div>
      </form>
      {loading && <LoadingComponent />}
    </motion.div>
  );
}
